import { Dialog as DialogPrimitive } from 'radix-ui'
import { XIcon } from 'lucide-react'
import type { ComponentProps } from 'react'

import { cn } from '@/lib/utils'

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger
const DialogPortal = DialogPrimitive.Portal
const DialogClose = DialogPrimitive.Close

function DialogOverlay({ className, ...props }: ComponentProps<typeof DialogPrimitive.Overlay>) {
    return (
        <DialogPrimitive.Overlay
            data-slot="dialog-overlay"
            className={cn(
                'fixed inset-0 z-50 bg-black/40 dark:bg-black/60',
                'data-[state=open]:animate-in data-[state=open]:fade-in-0',
                'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
                className
            )}
            {...props}
        />
    )
}

function DialogContent({
    className,
    children,
    showCloseButton = true,
    showGrabber = true,
    ...props
}: ComponentProps<typeof DialogPrimitive.Content> & {
    showCloseButton?: boolean
    /**
     * The bottom-sheet grab handle drawn below `sm`. Off for dialogs that anchor
     * themselves somewhere other than the bottom edge, where a handle would be a
     * promise the panel does not keep.
     */
    showGrabber?: boolean
}) {
    return (
        <DialogPortal>
            <DialogOverlay />
            <DialogPrimitive.Content
                data-slot="dialog-content"
                className={cn(
                    'fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4',
                    // Without a height bound and a scroller a tall dialog is sheared off top and
                    // bottom, taking the title and the footer buttons with it.
                    'max-h-[calc(100dvh-2rem)] overflow-y-auto',
                    'rounded-xl border border-border bg-popover p-5 text-popover-foreground shadow-xl',
                    /*
                     * On a phone a dialog is a bottom sheet. A box centred in the middle of
                     * the screen is the furthest point from the thumb and leaves dead margin
                     * on all four sides; anchoring to the bottom edge puts the content and
                     * its buttons where the hand already is. It rises into place rather than
                     * zooming, which is what makes it read as a sheet.
                     *
                     * Every override below is the counterpart of a base class, so at `sm` and
                     * up the centred dialog is exactly what it was. Callers that position
                     * themselves (the nav drawer, the command palette, the full-bleed picker)
                     * must restate their own values under `max-sm:` to win here.
                     */
                    'max-sm:bottom-0 max-sm:left-0 max-sm:top-auto max-sm:max-w-none',
                    'max-sm:translate-x-0 max-sm:translate-y-0',
                    'max-sm:max-h-[calc(100dvh-3rem)] max-sm:rounded-b-none max-sm:rounded-t-2xl',
                    'max-sm:p-4 max-sm:pb-[max(1rem,env(safe-area-inset-bottom))]',
                    showGrabber && 'max-sm:pt-6',
                    'duration-(--animate-duration)',
                    'data-[state=open]:animate-in data-[state=open]:fade-in-0',
                    'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
                    'sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95',
                    'max-sm:data-[state=open]:slide-in-from-bottom max-sm:data-[state=closed]:slide-out-to-bottom',
                    className
                )}
                {...props}>
                {/* The sheet's grab handle. Absolutely positioned so it never becomes a
                    grid item and cannot disturb the gap between the dialog's sections. */}
                {showGrabber && (
                    <span
                        aria-hidden
                        className="absolute inset-x-0 top-2 mx-auto h-1 w-9 rounded-full bg-input sm:hidden"
                    />
                )}
                {children}
                {showCloseButton && (
                    <DialogPrimitive.Close
                        data-slot="dialog-close"
                        className={cn(
                            // Isolated in the corner, so it grows for real under a coarse pointer
                            // rather than taking hit-slop that would reach into the header.
                            'absolute right-3 top-3 inline-flex size-7 items-center justify-center rounded-md text-muted-foreground',
                            'pointer-coarse:size-10 pointer-coarse:right-2 pointer-coarse:top-2',
                            'transition-colors hover:bg-surface-hover hover:text-foreground',
                            'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                            'disabled:pointer-events-none'
                        )}>
                        <XIcon className="size-4" />
                        <span className="sr-only">Close</span>
                    </DialogPrimitive.Close>
                )}
            </DialogPrimitive.Content>
        </DialogPortal>
    )
}

function DialogHeader({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="dialog-header"
            // The close button grows to 40px at right-2 under a coarse pointer, so it reaches
            // 48px in from the edge; the reserved gutter has to match or a long title runs under it.
            className={cn('flex flex-col gap-1 pr-8 pointer-coarse:pr-12', className)}
            {...props}
        />
    )
}

function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="dialog-footer"
            className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
            {...props}
        />
    )
}

function DialogTitle({ className, ...props }: ComponentProps<typeof DialogPrimitive.Title>) {
    return (
        <DialogPrimitive.Title
            data-slot="dialog-title"
            className={cn('text-base font-semibold leading-none tracking-tight', className)}
            {...props}
        />
    )
}

function DialogDescription({ className, ...props }: ComponentProps<typeof DialogPrimitive.Description>) {
    return (
        <DialogPrimitive.Description
            data-slot="dialog-description"
            className={cn('text-sm leading-relaxed text-muted-foreground', className)}
            {...props}
        />
    )
}

export {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogTrigger
}
